import axios from 'axios';

const API_URL = process.env.REACT_APP_API;

export interface RostroCliente {
  id: number;
  nombre: string;
  telefono?: string;
  /** Saldo REAL del monedero de la Wallet vinculado (tickets.saldo), no del rostro. */
  saldo: number;
  foto_url?: string;
  hikvision_face_id?: string;
  activo: boolean;
  evento_id?: number;

  // ─── Monedero de la Wallet vinculado ───
  wallet_user_id?: number | null;
  wallet_email?: string | null;
  /** Correo enmascarado para mostrar en pantallas a la vista del público. */
  wallet_email_mascarado?: string | null;
  wallet_nombre?: string | null;
  wallet_folio?: string | null;
  tiene_wallet?: boolean;
  /**
   * Presente solo en la respuesta de crear/actualizar: motivo por el que el
   * enrolamiento en el terminal HIKVISION falló. El registro sí se guardó en BD,
   * pero esa persona NO será reconocida hasta reenrolarla con otra foto.
   */
  hikvision_error?: string | null;
}

/** Resultado de la búsqueda de usuarios de Wallet para el combo de correos. */
export interface WalletUserBusqueda {
  id: number;
  nombre: string;
  email: string;
  folio: string | null;
  saldo: number;
}

export interface Producto {
  id: number;
  nombre: string;
  costo_sugerido: number;
  tipo?: string;
}

/** Puesto con día operativo abierto: es el que puede recibir la venta. */
export interface PuntoVentaFR {
  id: number;
  nombre: string;
  tipo?: string;
  /** Día operativo que el servidor resolvió para este puesto. Informativo. */
  id_dia: number;
  fecha?: string;
}

export interface ProductoCarrito extends Producto {
  /** Costo unitario tomado del catálogo del puesto. */
  precio: number;
  cantidad: number;
}

export interface TransaccionFR {
  transaccion_id: number;
  monto_total: number;
  saldo_restante: number;
  /** true = el backend detectó un reintento y devolvió el cobro ya registrado. */
  repetida?: boolean;
  cliente: { id: number; nombre: string };
  punto_venta?: { id: number; nombre: string };
  id_dia?: number;
  /** Ids de los renglones escritos en `ventas`: la compra ya existe para el negocio. */
  ventas_ids?: number[];
}

export interface FaceEvent {
  hikvision_face_id: string;
  name: string;
  similarity: number;
  capturedAt: string;
  rostroCliente?: RostroCliente;
}

// ─── Catálogo de Rostros ─────────────────────
export async function getRostrosService(token: string): Promise<RostroCliente[]> {
  const response = await axios.get(`${API_URL}rostroCatalogo`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data.doc.data;
}

export async function getRostroByIdService(id: number, token: string): Promise<RostroCliente> {
  const response = await axios.get(`${API_URL}rostroCatalogo/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data.doc.data;
}

export async function createRostroService(formData: FormData, token: string): Promise<RostroCliente> {
  const response = await axios.post(`${API_URL}rostroCatalogo`, formData, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
  });
  return response.data.doc.data;
}

export async function updateRostroService(id: number, formData: FormData, token: string): Promise<RostroCliente> {
  const response = await axios.put(`${API_URL}rostroCatalogo/${id}`, formData, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
  });
  return response.data.doc.data;
}

export async function deleteRostroService(id: number, token: string): Promise<void> {
  await axios.delete(`${API_URL}rostroCatalogo/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
}

export async function recargarSaldoService(id: number, monto: number, token: string): Promise<{ id: number; saldo: number }> {
  const response = await axios.post(
    `${API_URL}rostroCatalogo/${id}/recarga`,
    { monto },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data.doc.data;
}

// ─── Pago Face Recognition ───────────────────
/** Puestos a los que se les puede atribuir la venta ahora mismo. */
export async function getPuntosVentaFRService(token: string): Promise<PuntoVentaFR[]> {
  const response = await axios.get(`${API_URL}faceRecognition/puntosVenta`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data.doc.data ?? [];
}

/**
 * Productos del puesto. Sin puntoVentaId devuelve el catálogo completo del
 * evento, que ya no se usa en la pantalla de cobro pero se conserva por
 * compatibilidad.
 */
export async function getProductosFRService(token: string, puntoVentaId?: number): Promise<Producto[]> {
  const url = puntoVentaId
    ? `${API_URL}faceRecognition/productos?punto_venta_id=${puntoVentaId}`
    : `${API_URL}faceRecognition/productos`;
  const response = await axios.get(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data.doc.data ?? [];
}

/**
 * Cobra la compra. Solo se envía QUÉ y CUÁNTO: el precio y el total los calcula
 * el servidor desde el catálogo del puesto. Mandar el precio desde aquí
 * permitiría cobrarse un producto de $500 por un peso.
 */
export async function procesarPagoFRService(
  id_rostro_cliente: number,
  id_punto_venta: number,
  productos: ProductoCarrito[],
  token: string,
  idempotencyKey?: string
): Promise<TransaccionFR> {
  const response = await axios.post(
    `${API_URL}faceRecognition/pagar`,
    {
      id_rostro_cliente,
      id_punto_venta,
      productos: productos.map((p) => ({ id_producto: p.id, cantidad: p.cantidad })),
      idempotency_key: idempotencyKey,
    },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data.doc.data;
}

export async function getUltimoEventoFRService(token: string): Promise<FaceEvent | null> {
  const response = await axios.get(`${API_URL}faceRecognition/ultimoEvento`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data.doc.data;
}

/**
 * Canjea el JWT de sesión por un ticket corto para SSE y snapshot.
 * Ver src/utils/streamTicket.js en el backend para el razonamiento.
 */
export async function getStreamTicketFRService(
  token: string
): Promise<{ ticket: string; expiresIn: number }> {
  const response = await axios.post(
    `${API_URL}faceRecognition/streamTicket`,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data.doc.data;
}

/**
 * Busca usuarios de Wallet por nombre o correo, para el combo de "Nuevo Rostro".
 * Reutiliza el endpoint que ya existía en el módulo de tickets.
 * Exige mínimo 3 caracteres y el backend limita a 20 resultados.
 */
export async function buscarWalletUsersService(
  texto: string,
  token: string
): Promise<WalletUserBusqueda[]> {
  const response = await axios.get(
    `${API_URL}tickets/consultar/wallet-users/nombre/${encodeURIComponent(texto)}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data.doc.data ?? [];
}

export function getSnapshotUrl(): string {
  return `${API_URL}faceRecognition/snapshot`;
}
